import React, { useLayoutEffect, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { initializeApp } from 'firebase/app';
import appRoutes from './appRoutes';
import { isPermittedRoute } from './utils/routeUtils';
import { getCurrentUser } from './routes/login/api';
import useLoginModal from './routes/login/useLoginModal';

const firebaseConfig = {
  apiKey: 'AIzaSyCGYlWZDDyVIwQBcU1NawNrXpPD8ZyEsJY',
  authDomain: 'mvp-tmt.firebaseapp.com',
  projectId: 'mvp-tmt',
  storageBucket: 'mvp-tmt.appspot.com',
  messagingSenderId: '594858045527',
  appId: '1:594858045527:web:b1fd17966bd8fcfe5af312',
  measurementId: 'G-K1PV700BXD',
};

const ProtectedRoute = (props) => {
  const { children, route } = props;
  const { userData, activeRequests } = useSelector((state) => state.app);
  const loginModal = useLoginModal();
  const loginPromptedRef = useRef(false);

  const isLoading = activeRequests?.includes('fetch-current-user') || typeof userData?.isAdmin === 'undefined';
  const isPermitted = isPermittedRoute(route, userData);
  const shouldOpenLoginModal = route?.permission === 'email' && !userData?.email;

  useEffect(() => {
    if (isLoading) return;

    if (!isPermitted && shouldOpenLoginModal && !loginPromptedRef.current) {
      loginModal.onOpen();
      loginPromptedRef.current = true;
    }

    if (isPermitted || !shouldOpenLoginModal) {
      loginPromptedRef.current = false;
    }
  }, [isLoading, isPermitted, shouldOpenLoginModal, loginModal]);

  const Layout = route?.layout ?? null;
  if (isLoading) return (<div>loading</div>);

  if (isPermitted) return Layout ? <Layout>{children}</Layout> : (children);
  if (shouldOpenLoginModal) return null;

  return <Navigate to="/" replace />;
};

const AppRoutes = () => {
  const { redirect } = useSelector((state) => state.app);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { userData } = useSelector((state) => state.app);

  useLayoutEffect(() => {
    if (localStorage?.getItem('tmt-sessionToken')) {
      getCurrentUser();
    } else {
      dispatch({ type: 'APP:USER_DATA:SET', data: {} });
    }

    const app = initializeApp(firebaseConfig);
    if (process.env.appConfig.env === 'prod') {
      //Google tag (gtag.js)
      const script1 = document.createElement('script');
      script1.async = true;
      script1.src = 'https://www.googletagmanager.com/gtag/js?id=AW-16710198066';
      document.head.appendChild(script1);

      const script2 = document.createElement('script');
      script2.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', 'AW-16710198066');
      `;
      document.head.appendChild(script2);
    }
  }, []);

  useEffect(() => {
    if (redirect?.pathname) {
      navigate(redirect.pathname, redirect?.options || {});
      dispatch({ type: 'APP:REDIRECT:SET', data: {} });
    }
  }, [redirect]);

  useEffect(() => {
    if (window.innerWidth > 768) return;

    const applyOffset = () => {
      const iframe = document.querySelector('.widget-visible iframe');
      if (iframe) {
        iframe.style.setProperty('bottom', '70px', 'important');
      }
    };

    const observer = new MutationObserver(() => {
      applyOffset();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style'],
    });

    applyOffset();

    return () => observer.disconnect();
  }, []);

  return (
    <Routes>
      {appRoutes.map((route) => {
        const Component = route.element;
        let Element = <Component />;

        if (route.permission) Element = (<ProtectedRoute route={route}><Component /></ProtectedRoute>);

        return (
          <Route
            key={route.id}
            index={route.index}
            path={route.path}
            element={Element}
          />
        );
      })}
    </Routes>
  );
};

export default <AppRoutes />;
